import { EditOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Descriptions, Empty, Flex } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { IngredientReferencePriceModal } from "../../components/IngredientReferencePriceModal.tsx";
import { useCurrentSession } from "../../context/CurrentSessionContext.tsx";
import { formatNumber } from "../../utils/nutritionFormat.ts";
import { formatEur } from "../../utils/priceFormat.ts";
import { useSaving } from "../../hooks/useSaving.tsx";
import { useIngredient } from "./IngredientContext.tsx";

export const IngredientPriceTab = () => {
  const { t, i18n } = useTranslation();
  const { ingredient, units, readOnly, refetch } = useIngredient();
  const { trackSaving } = useSaving();
  const [modalOpen, setModalOpen] = useState(false);

  const { team } = useCurrentSession();
  const currency = team?.currency;

  const price = ingredient.referencePrice;
  const formatPrice = (amount: number) =>
    currency == null ? formatNumber(amount, i18n.language, 2) : formatEur(amount, i18n.language, currency);

  const priceSummary = (p: NonNullable<typeof price>) => {
    const vatMode = t(p.includesVat ? "ingredients.price.inlineWithVat" : "ingredients.price.inlineWithoutVat");
    const vat = p.vatRate == null ? vatMode : `${vatMode} ${formatNumber(p.vatRate, i18n.language)}%`;
    const unitCode = units.find((u) => u.id === p.unitId)?.code ?? "";
    return t("ingredients.price.priceSummary", {
      price: formatPrice(p.price),
      vat,
      quantity: `${formatNumber(p.quantity, i18n.language)} ${unitCode}`,
    });
  };

  return (
    <Flex align="flex-start" gap="middle" vertical>
      {!readOnly &&
        (price == null ? (
          <Button icon={<PlusOutlined />} onClick={() => setModalOpen(true)} type="primary">
            {t("ingredients.price.setButton")}
          </Button>
        ) : (
          <Button color="primary" icon={<EditOutlined />} onClick={() => setModalOpen(true)} variant="outlined">
            {t("ingredients.price.changeButton")}
          </Button>
        ))}
      {price == null ? (
        <Empty description={t("ingredients.price.empty")} />
      ) : (
        <Descriptions
          bordered
          column={1}
          items={[
            {
              key: "price",
              label: t("ingredients.price.price"),
              children: priceSummary(price),
            },
            {
              key: "perKgWithoutVat",
              label: t("ingredients.price.perKgWithoutVat"),
              children: formatPrice(price.netPerGram * 1000),
            },
            {
              key: "perKgWithVat",
              label: t("ingredients.price.perKgWithVat"),
              children: formatPrice(price.grossPerGram * 1000),
            },
          ]}
          size="small"
        />
      )}
      <IngredientReferencePriceModal
        ingredient={ingredient}
        onClose={() => setModalOpen(false)}
        onSaved={() => trackSaving(() => refetch())}
        open={modalOpen}
        units={units}
      />
    </Flex>
  );
};
